import { watch } from 'vue';
import type { Observable } from 'rxjs';
import { finalize, forkJoin, of } from 'rxjs';
import { watchThrottled } from '@vueuse/core';
import { difference, uniq } from 'lodash';

import {
  useItineraryStore,
  useMetaStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useCriteriaStore } from '@apps/main/new-desktop/core/stores/criteria/criteriaStore';
import { useProductDetailStore } from '@apps/main/new-desktop/core/stores/productDetail/productDetailStore';

import { assertValue } from '@libs/utils';
import { IngredientType } from '@libs/trip-planner-interfaces';
import type {
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  AvailableTransportIngredientResponseWithStaticInfo,
  CarrierStaticInfo,
  Dictionary,
  FerryIngredientResponse,
  FlightIngredientResponse,
  HotelBookableAndLockableOption,
  HotelBookableOptionRoom,
  IngredientId,
  PortStaticInfo,
  TransportListResponse,
} from '@libs/trip-planner-interfaces';
import {
  isAvailableFerryIngredientResponse,
  isAvailableFlightIngredientResponse,
  isAvailableTransportIngredientResponse,
  isFlightIngredientResponse,
} from '@libs/search-service-smart-search';

import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import { flatIatas } from '../recipe/ingredients/helpers';

export function getHotelBookableOptionRoomUniqueKey(
  room: HotelBookableOptionRoom
): string {
  let uniqueKey = room.description + JSON.stringify(room.pax) + room.boardBasis;

  if (room.roomTypeId) {
    uniqueKey += room.roomTypeId.toString();
  } else {
    uniqueKey += room.roomCode;
  }

  return uniqueKey;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useProductAvailabilityViewModel(model: TripPlannerModel) {
  const itineraryStore = useItineraryStore();
  const metaStore = useMetaStore();
  const tripStore = useTripStore();
  const criteriaStore = useCriteriaStore();
  const productDetailStore = useProductDetailStore();

  watch(
    () => criteriaStore.accommodationProductAvailabilityCriteria,
    (criteria, oldCriteria) => {
      if (!criteria) {
        return;
      }

      productDetailStore.incrementAccommodationDetailAvailabilityLoadingCounter();
      productDetailStore.clearDestinationProductDetailAvailability();

      if (criteria.productCode !== oldCriteria?.productCode) {
        productDetailStore.clearDestinationProductDetailStaticInfo();
      }

      const ingredient =
        criteria.recipe.ingredients?.[criteria.ingredientIndex];

      if (!ingredient) {
        return;
      }

      model
        .searchDetailStaticInfo(criteria.productCode, ingredient.ingredientId)
        .subscribe((detailStaticInfo) => {
          productDetailStore.setDestinationProductDetailStaticInfo(
            detailStaticInfo
          );
        });

      model
        .searchAccommodationAvailability({
          userCriteria: criteria,
          businessCriteria: criteriaStore.businessCriteria,
        })
        .pipe(
          finalize(() => {
            productDetailStore.decrementAccommodationDetailAvailabilityLoadingCounter();
          })
        )
        .subscribe((cheapestStay) => {
          const isFromMeta = metaStore.stayIsFromMeta(cheapestStay);
          const isLocked = !!tripStore.stayIsLocked(
            cheapestStay,
            productDetailStore.destinationProductDetailIngredientId
          );

          const option: HotelBookableAndLockableOption = {
            addToBasketToken: cheapestStay.addToBasketToken,
            code: cheapestStay.code,
            isFromMeta,
            isLocked,
            isRefundable: cheapestStay.isRefundable,
            rooms: cheapestStay.rooms,
            totalPrice: cheapestStay.totalPrice,
            prices: cheapestStay.prices,
          };

          const uniqueStayKey = option.rooms
            .map(getHotelBookableOptionRoomUniqueKey)
            .join();

          productDetailStore.updateAccommodationDetailAvailability(
            uniqueStayKey,
            option
          );
        });
    }
  );

  watch(
    () => criteriaStore.activityProductAvailabilityCriteria,
    (criteria) => {
      if (!criteria) {
        return;
      }
      productDetailStore.incrementAccommodationDetailAvailabilityLoadingCounter();

      productDetailStore.clearDestinationProductDetailAvailability();
      productDetailStore.clearDestinationProductDetailStaticInfo();

      model
        .searchDetailStaticInfo(
          criteria.productCode,
          criteria.parentIngredientId
        )
        .subscribe((detailStaticInfo) => {
          productDetailStore.setDestinationProductDetailStaticInfo(
            detailStaticInfo
          );
        });

      model
        .searchActivityAvailability({
          userCriteria: criteria,
          businessCriteria: criteriaStore.businessCriteria,
        })
        .pipe(
          finalize(() => {
            productDetailStore.decrementAccommodationDetailAvailabilityLoadingCounter();
          })
        )
        .subscribe((response) => {
          productDetailStore.setActivityDetailAvailability(response);
        });
    }
  );

  watchThrottled(
    () => criteriaStore.transportAvailabilityCriteria,
    (criteria, oldCriteria) => {
      if (
        criteria &&
        JSON.stringify(criteria) !== JSON.stringify(oldCriteria)
      ) {
        tripStore.clearTransportList();
        tripStore.increaseTransportListSearchCounter();

        model
          .searchTransportAvailability(criteria, criteriaStore.businessCriteria)
          .subscribe((transportListResponse) => {
            if (!criteria.recipe.ingredients) {
              tripStore.decreaseTransportListSearchCounter();
              return;
            }

            const ingredient =
              criteria.recipe.ingredients[criteria.ingredientIndex];

            assertValue(
              ingredient,
              'ProductAvailabilityViewModel.ts > model.searchTransportAvailability > ingredient'
            );

            const criteriaIngredientDate = itineraryStore.getIngredientDate(
              ingredient.ingredientId
            );

            if (!criteriaIngredientDate) {
              tripStore.decreaseTransportListSearchCounter();
              return;
            }

            setTransportListWithStaticInfo(
              transportListResponse,
              ingredient.ingredientId
            );
          });
      }
    },
    { throttle: 200 }
  );

  function setTransportListWithStaticInfo(
    transportListResponse: TransportListResponse,
    ingredientId?: IngredientId
  ): void {
    const carrierStaticInfoObservable = searchMissingCarrierStaticInfo(
      transportListResponse.transports
    );

    carrierStaticInfoObservable.subscribe((carrierStaticInfo) =>
      tripStore.insertCarrierStaticInfo(carrierStaticInfo)
    );

    const portStaticInfoObservable = searchMissingPortStaticInfo(
      transportListResponse.transports
    );

    portStaticInfoObservable.subscribe(
      ({ missingAirportStaticInfo, missingSeaportStaticInfo }) => {
        tripStore.insertPortStaticInfo(
          missingAirportStaticInfo,
          IngredientType.Flight
        );
        tripStore.insertPortStaticInfo(
          missingSeaportStaticInfo,
          IngredientType.Ferry
        );
      }
    );

    const selectedIngredient = ingredientId
      ? itineraryStore.getTransportIngredientResponse(ingredientId)
      : undefined;

    forkJoin({
      carrierStaticInfoObservable,
      portStaticInfoObservable,
    }).subscribe(() => {
      tripStore.setTransportList(
        getTransportListWithStaticInfo(transportListResponse.transports),
        selectedIngredient
          ? getTransportListWithStaticInfo([selectedIngredient])[0]
          : undefined
      );
      tripStore.decreaseTransportListSearchCounter();
    });
  }

  function searchMissingCarrierStaticInfo(
    transports: (
      | AvailableFlightIngredientResponse
      | AvailableFerryIngredientResponse
    )[]
  ): Observable<Dictionary<CarrierStaticInfo>> {
    const transportListCarriers: string[] = uniq(
      transports.flatMap((transport) => {
        const carriers = transport.carriers;

        if (isFlightIngredientResponse(transport)) {
          const operatingCarriers = transport.legs.flatMap((leg) =>
            leg.segments.flatMap((segment) => segment.operatingCarrier)
          );
          carriers.push(...operatingCarriers);
        }

        return carriers;
      })
    );

    const existingCarriers = tripStore.getCarrierCodesWithStaticInfo();

    const newCarriers = difference(transportListCarriers, existingCarriers);

    return model.searchCarrierStaticInfo(newCarriers);
  }

  function searchMissingPortStaticInfo(
    transports: (
      | AvailableFlightIngredientResponse
      | AvailableFerryIngredientResponse
    )[]
  ): Observable<{
    missingAirportStaticInfo: Dictionary<PortStaticInfo>;
    missingSeaportStaticInfo: Dictionary<PortStaticInfo>;
  }> {
    if (!transports.length) {
      return of({
        missingAirportStaticInfo: {},
        missingSeaportStaticInfo: {},
      });
    }

    const flightTransports = transports.filter(
      isAvailableFlightIngredientResponse
    );
    const ferryTransports = transports.filter(
      isAvailableFerryIngredientResponse
    );

    const missingAirportStaticInfo =
      searchMissingAirportStaticInfo(flightTransports);

    const missingSeaportStaticInfo =
      searchMissingSeaportStaticInfo(ferryTransports);

    return forkJoin({ missingAirportStaticInfo, missingSeaportStaticInfo });
  }

  function searchMissingAirportStaticInfo(
    flightTransports: AvailableFlightIngredientResponse[]
  ): Observable<Dictionary<PortStaticInfo>> {
    if (!flightTransports.length) {
      return of({});
    }

    const flightListIatas = uniq(
      flightTransports.flatMap((transport) =>
        transport.legs.flatMap((leg) => flatIatas(leg.segments))
      )
    );

    const existingFlightIatas = tripStore.getPortCodesWithStaticInfo(
      IngredientType.Flight
    );

    const newFlightIatas = difference(flightListIatas, existingFlightIatas);

    return model.searchAirportStaticInfo(newFlightIatas);
  }

  function searchMissingSeaportStaticInfo(
    ferryTransports: AvailableFerryIngredientResponse[]
  ): Observable<Dictionary<PortStaticInfo>> {
    if (!ferryTransports.length) {
      return of({});
    }

    const ferryListIatas = uniq(
      ferryTransports.flatMap((transport) => [
        transport.departureIata,
        transport.arrivalIata,
      ])
    );

    const existingFerryIatas = tripStore.getPortCodesWithStaticInfo(
      IngredientType.Ferry
    );

    const newFerryIatas = difference(ferryListIatas, existingFerryIatas);

    return model.searchSeaportStaticInfo(newFerryIatas);
  }

  function getTransportListWithStaticInfo(
    transportAvailabilityList: (
      | FlightIngredientResponse
      | FerryIngredientResponse
    )[]
  ): AvailableTransportIngredientResponseWithStaticInfo[] {
    return transportAvailabilityList.reduce(
      (
        transportsWithStaticInfo: AvailableTransportIngredientResponseWithStaticInfo[],
        transport
      ) => {
        const isAvailableTransport =
          isAvailableTransportIngredientResponse(transport);

        const staticInfo = isAvailableTransport
          ? tripStore.getTransportStaticInfo(transport)
          : undefined;

        if (isAvailableTransport && staticInfo) {
          transportsWithStaticInfo.push({ ...transport, staticInfo });
        }

        return transportsWithStaticInfo;
      },
      []
    );
  }
}
